<template>
    <form name="event-form" id="event-form" class="event-form" novalidate @submit="onSubmit" @input="markDirty" @change="markDirty">
        <input type="hidden" name="id" v-model="model.id">

        <p class="form-required-note"><span aria-hidden="true">*</span> Required fields</p>

        <div v-if="Object.keys(fieldErrors).length" class="form-alert" role="alert" aria-live="assertive" tabindex="-1">
            <strong>Review the highlighted fields.</strong>
            <span>{{ formErrorMessage }}</span>
        </div>

        <div class="form-row event-form-sections">
            <section class="col-lg-6 mb-4 mb-lg-0" aria-labelledby="event-details-heading">
                <div class="event-form-section">
                    <div class="section-heading">
                        <div>
                            <p class="section-eyebrow">EVENT</p>
                            <h2 id="event-details-heading">Event details</h2>
                        </div>
                        <i class="pi pi-calendar" aria-hidden="true"></i>
                    </div>

                <div class="form-group">
                    <label for="name">Event name <span class="required-indicator" aria-hidden="true">*</span></label>
                    <InputText id="name" v-model="model.name" class="form-control" :class="{ 'p-invalid': fieldErrors.name }" aria-describedby="name-help name-error" required></InputText>
                    <small id="name-help" class="form-help">Use the public-facing name delegates will recognize.</small>
                    <small v-if="fieldErrors.name" id="name-error" class="field-error">{{ fieldErrors.name }}</small>
                </div>

                <div class="form-group">
                    <label for="description">Event description <span class="required-indicator" aria-hidden="true">*</span></label>
                    <Textarea id="description" v-model="model.description" class="form-control" :class="{ 'p-invalid': fieldErrors.description }" aria-describedby="description-help description-error" rows="7" required></Textarea>
                    <small id="description-help" class="form-help">Summarize the purpose, audience, and key information for the event.</small>
                    <small v-if="fieldErrors.description" id="description-error" class="field-error">{{ fieldErrors.description }}</small>
                </div>

                <div class="row">
                    <div class="col">
                        <div class="form-group">
                            <label for="startDate">Start date <span class="required-indicator" aria-hidden="true">*</span></label>
                            <Calendar inputId="startDate" v-model="model.startDate" :inputClass="['form-control', { 'p-invalid': fieldErrors.startDate || fieldErrors.dateRange }]" :showIcon="true" iconDisplay="input" dateFormat="yy-mm-dd" aria-describedby="date-help start-date-error" required />
                            <small v-if="fieldErrors.startDate" id="start-date-error" class="field-error">{{ fieldErrors.startDate }}</small>
                        </div>
                    </div>
                    <div class="col">
                        <div class="form-group">
                            <label for="endDate">End date <span class="required-indicator" aria-hidden="true">*</span></label>
                            <Calendar inputId="endDate" v-model="model.endDate" :inputClass="['form-control', { 'p-invalid': fieldErrors.endDate || fieldErrors.dateRange }]" :showIcon="true" iconDisplay="input" dateFormat="yy-mm-dd" aria-describedby="date-help end-date-error" required />
                            <small v-if="fieldErrors.endDate" id="end-date-error" class="field-error">{{ fieldErrors.endDate }}</small>
                        </div>
                    </div>
                </div>
                <small id="date-help" class="form-help date-help">Select dates in YYYY-MM-DD format. The end date must not be before the start date.</small>
                <small v-if="fieldErrors.dateRange" class="field-error">{{ fieldErrors.dateRange }}</small>
            </div>
            </section>

            <section class="col-lg-6" aria-labelledby="location-details-heading">
                <div class="event-form-section">
                    <div class="section-heading">
                        <div>
                            <p class="section-eyebrow">VENUE</p>
                            <h2 id="location-details-heading">Location details</h2>
                        </div>
                        <i class="pi pi-map-marker" aria-hidden="true"></i>
                    </div>

                <div class="form-group">
                    <label for="locationName">Location name <span class="required-indicator" aria-hidden="true">*</span></label>
                    <InputText id="locationName" v-model="model.locationName" class="form-control" :class="{ 'p-invalid': fieldErrors.locationName }" aria-describedby="location-name-help location-name-error" required></InputText>
                    <small id="location-name-help" class="form-help">For example, venue, hotel, or meeting room name.</small>
                    <small v-if="fieldErrors.locationName" id="location-name-error" class="field-error">{{ fieldErrors.locationName }}</small>
                </div>

                <div class="form-group">
                    <label for="locationDescription">Location description <span class="required-indicator" aria-hidden="true">*</span></label>
                    <Textarea id="locationDescription" v-model="model.locationDescription" class="form-control" :class="{ 'p-invalid': fieldErrors.locationDescription }" aria-describedby="location-description-help location-description-error" rows="7" required></Textarea>
                    <small id="location-description-help" class="form-help">Add address, access notes, or directions attendees may need.</small>
                    <small v-if="fieldErrors.locationDescription" id="location-description-error" class="field-error">{{ fieldErrors.locationDescription }}</small>
                </div>

                <div class="coordinate-heading">
                    <span>Map coordinates</span>
                    <small>Optional — enter decimal degrees if available.</small>
                </div>
                <div class="row">
                    <div class="col">
                        <div class="form-group">
                            <label for="latitude">Latitude</label>
                            <InputText class="form-control" v-model="model.latitude" id="latitude"></InputText>
                        </div>
                    </div>
                    <div class="col">
                        <div class="form-group">
                            <label for="longitude">Longitude</label>
                            <InputText class="form-control" v-model="model.longitude" id="longitude"></InputText>
                        </div>
                    </div>
                </div>

            </div>
            </section>

        </div>

        <div class="form-actions">
            <p class="form-actions-note">{{ hasUnsavedChanges ? 'You have unsaved changes.' : 'All changes are saved when you submit.' }}</p>
            <div class="form-actions-buttons">
                <button type="button" class="btn btn-outline-secondary mr-sm-1" @click="onCancel">Cancel</button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                    <span v-if="saving" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    {{ saving ? 'Saving…' : (isNew ? 'Create event' : 'Save changes') }}
                </button>
            </div>
        </div>

    </form>

    <ConfirmDialog></ConfirmDialog>
</template>

<script>
import {defineComponent} from 'vue'
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Calendar from "primevue/calendar";
import apiService from "@/services/ApiService";

export default defineComponent({
    name: "EventForm",
    components: {
        InputText, Textarea, Calendar
    },
    props: {
        id: {
            type: String,
            required: false
        },
        isNew: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            model: {
                id: '',
                name: '',
                description: '',
                startDate: '',
                endDate: '',
                locationName: '',
                locationDescription: '',
                latitude: '',
                longitude: '',
                status: ''
            },
            saving: false,
            hasUnsavedChanges: false,
            fieldErrors: {}
        }
    },
    computed: {
        formErrorMessage() {
            return Object.values(this.fieldErrors)[0];
        }
    },
    methods: {
        onSubmit(event) {
            event.preventDefault();
            if (!this.validateForm()) {
                return;
            }
            this.saving = true;

            let url = '/event/edit';
            let successMessage = 'Event has been updated';
            let errorMessage = 'Failed to update event';
            if(this.isNew){
                url = '/event/add';
                successMessage = 'Event has been added';
                errorMessage = 'Failed to add event';
            }

            apiService.post(url, this.model)
                .then(() => {
                    this.hasUnsavedChanges = false;
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: successMessage,
                        life: 5000
                    });
                })
                .catch(error => {
                    console.log(error);
                    this.$toast.add({
                        severity: 'error',
                        summary: errorMessage,
                        detail: error.response.data.Message,
                        life: 5000
                    });
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        onCancel() {
            if (!this.hasUnsavedChanges) {
                this.$router.push('/event');
                return;
            }

            this.$confirm.require({
                message: 'Discard your unsaved event changes?',
                header: 'Discard changes',
                icon: 'pi pi-exclamation-triangle',
                acceptClass: 'p-button-danger',
                accept: () => this.$router.push('/event')
            });
        },
        markDirty() {
            this.hasUnsavedChanges = true;
            if (Object.keys(this.fieldErrors).length) {
                this.validateForm();
            }
        },
        validateForm() {
            const errors = {};
            const requiredFields = [
                ['name', 'Event name is required.'],
                ['description', 'Event description is required.'],
                ['startDate', 'Start date is required.'],
                ['endDate', 'End date is required.'],
                ['locationName', 'Location name is required.'],
                ['locationDescription', 'Location description is required.']
            ];

            requiredFields.forEach(([field, message]) => {
                if (!this.model[field]) {
                    errors[field] = message;
                }
            });

            if (this.model.startDate && this.model.endDate && new Date(this.model.endDate) < new Date(this.model.startDate)) {
                errors.dateRange = 'End date must be the same as or later than the start date.';
            }

            this.fieldErrors = errors;
            return Object.keys(errors).length === 0;
        },
        getEventById(id) {
            apiService.get(`/event/${id}`)
                .then(response => {
                    this.model = response.data;
                    this.hasUnsavedChanges = false;
                }).catch(error => {
                console.log(error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to get event',
                    detail: error.response.data.Message,
                    life: 5000
                });
            });
        }
    },
    mounted() {

        if(!this.isNew){
            this.getEventById(this.id);
        }

    }
})
</script>

<style scoped>
.event-form {
    max-width: 1280px;
}

.form-required-note {
    color: #5f6570;
    font-size: 0.875rem;
    margin: 0 0 1rem;
}

.form-required-note span,
.required-indicator {
    color: #c62828;
    font-weight: 700;
}

.form-alert {
    align-items: flex-start;
    background: #fff1f1;
    border: 1px solid #f5c2c7;
    border-radius: 0.5rem;
    color: #9d2525;
    display: flex;
    flex-direction: column;
    font-size: 0.9rem;
    gap: 0.2rem;
    margin-bottom: 1rem;
    padding: 0.8rem 1rem;
}

.event-form-section {
    background: #ffffff;
    border: 1px solid #e2e6ec;
    border-radius: 0.65rem;
    box-shadow: 0 0.15rem 0.65rem rgba(58, 59, 69, 0.05);
    height: 100%;
    padding: 1.5rem;
}

.section-heading {
    align-items: flex-start;
    border-bottom: 1px solid #edf0f4;
    display: flex;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
}

.section-heading h2 {
    color: #2b2f36;
    font-size: 1.15rem;
    font-weight: 600;
    margin: 0;
}

.section-eyebrow {
    color: #426cc6;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    margin: 0 0 0.25rem;
}

.section-heading > .pi {
    color: #426cc6;
    font-size: 1.2rem;
    padding-top: 0.25rem;
}

.form-group label,
.coordinate-heading > span {
    color: #343a40;
    font-size: 0.9rem;
    font-weight: 600;
}

.form-help,
.coordinate-heading small {
    color: #6c757d;
    display: block;
    font-size: 0.78rem;
    line-height: 1.35;
    margin-top: 0.35rem;
}

.date-help {
    margin: -0.8rem 0 1rem;
}

.field-error {
    color: #c62828;
    display: block;
    font-size: 0.8rem;
    margin-top: 0.35rem;
}

.coordinate-heading {
    border-top: 1px solid #edf0f4;
    margin: 1.25rem 0 1rem;
    padding-top: 1rem;
}

.form-actions {
    align-items: center;
    border-top: 1px solid #e2e6ec;
    display: flex;
    justify-content: space-between;
    margin-top: 1.5rem;
    padding-top: 1.25rem;
}

.form-actions-note {
    color: #6c757d;
    font-size: 0.85rem;
    margin: 0;
}

.form-actions-buttons {
    display: flex;
}

@media (max-width: 767.98px) {
    .event-form-section {
        padding: 1.15rem;
    }

    .form-actions {
        align-items: stretch;
        flex-direction: column;
        gap: 1rem;
    }

    .form-actions-buttons {
        justify-content: flex-end;
    }
}
</style>
